import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, Search, AlertCircle, ExternalLink } from 'lucide-react';
import api from '../../services/api';

const PAGE_SIZE = 20;

const BusinessLimits = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Debounce search input so we don't fire on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0); // reset to first page on new search
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (searchDebounced) params.search = searchDebounced;

    api.get('/api/admin/businesses', { params })
      .then(res => {
        const data = res.data;
        setBusinesses(Array.isArray(data) ? data : (data.businesses || []));
        setTotal(data.total ?? (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => setError('Failed to load businesses'))
      .finally(() => setLoading(false));
  }, [page, searchDebounced]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Business Limits</h1>
        <p className="text-gray-600 mt-1">View plan and subscription status per business. Click a row to manage limits.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              <CardTitle>All Businesses ({total})</CardTitle>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search businesses..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {businesses.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No businesses found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium text-gray-600">Business</th>
                    <th className="text-left p-3 font-medium text-gray-600">Plan</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-left p-3 font-medium text-gray-600">Subscription</th>
                    <th className="text-left p-3 font-medium text-gray-600">Created</th>
                    <th className="text-left p-3 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {businesses.map(business => (
                    <tr
                      key={business.id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/businesses/${business.id}`)}
                    >
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{business.name}</div>
                        <div className="text-xs text-gray-400">{business.owner_email || '—'}</div>
                      </td>
                      <td className="p-3 text-gray-700">
                        {business.plan_name || business.plan?.name || '—'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {business.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.subscription_status === 'active'
                            ? 'bg-blue-100 text-blue-700'
                            : business.subscription_status === 'trialing'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {business.subscription_status || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3 text-gray-500 text-xs">
                        {business.created_at
                          ? new Date(business.created_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-3">
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-gray-500">
                Page {page + 1} of {totalPages} ({total} total)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessLimits;
